/**
 *
 * This function allows to copy the content of a div to the clipboard by using a button. The content will be copied to the clipboard and an alert will be shown.
 * @module copyToClipboard
 * @param {Object} param0 - An object containing two properties: buttonToCopy and contentToCopy.
 * @param {string} param0.buttonToCopy - A string representing the selector of the button that triggers the copy action.
 * @param {string} param0.contentToCopy - A string representing the selector of the content to be copied.
 */

export default async function copyToClipboard({buttonToCopy, contentToCopy}) {
	const otherContent = contentToCopy;
	for (const item of document.querySelectorAll(contentToCopy)) {
		item.setAttribute('aria-live', 'polite');
	}
	for (const [index, button] of document.querySelectorAll(buttonToCopy).entries()) {
		button.addEventListener('click', async () => {
			const selectedElement = document.querySelectorAll(contentToCopy)[index];
			const content = selectedElement.nodeName === 'INPUT' ? selectedElement.value : selectedElement.innerHTML;
			await copyStringToClipboard(content);
			showAlertBox(index, contentToCopy, otherContent);
		});
	}
}

/**
 * This function copies a string to the clipboard.
 * @param {string} string - The string to be copied to the clipboard.
 *
 * Writing to the clipboard can only be done in a secure context.
 * This means that the button will only work on a https website.
 * In order to test this in local in chrome go to chrome://flags
 * and enable "Insecure origins treated as secure" and give it the origin you want.
**/

async function copyStringToClipboard(string) {
	await navigator.clipboard.writeText(string);
}

/**
 * This variable represents the HTML of an alert box.
 */
const alertBox = `
		<aside id="eds-c-status-message--copy" class="eds-c-status-message--closable eds-c-status-message eds-c-status-message--small eds-c-status-message--success u-padding-none">
			<svg  aria-hidden="true" class="eds-c-status-message__icon u-margin-top-none" width="24" height="24" role="img"
			 aria-label="success">
				<use href="/static/img/svg-sprite.svg#success"></use>
			</svg>
			<div class="eds-c-status-message__message u-font-size-small" tabindex="-1">
				<span role="alert">Copied to clipboard.</span>
				<button id="copy-values-alert-close" type="button"
				class="c-button--unstyled-in-brackets" title="Close">close
				</button>
			</div>
		</aside>`;

/**
 * The showAlertBox function is used to display an alert box when a copy action is performed.
 * It first removes any existing alert box, then creates a new one and inserts it after the copied content.
 * The alert box is hidden when the close button is clicked.
 *
 * @param {number} index - The index of the content that was copied. This is used to select the correct content when multiple elements match the contentToCopy selector.
 * @param {string} contentToCopy - A string representing the selector of the content to be copied. This is used to select the correct elements to make visible again.
 */

function showAlertBox(index, contentToCopy, otherContent) {
	removeAlertBox(otherContent);
	const alertBoxHtml = new DOMParser().parseFromString(alertBox, 'text/html');
	const contentToCopySelected = document.querySelectorAll(contentToCopy);
	contentToCopySelected[index].style.display = 'none';
	contentToCopySelected[index].after(alertBoxHtml.body.firstChild);

	const closeButton = document.querySelector('#copy-values-alert-close');
	closeButton.addEventListener('click', () => {
		removeAlertBox(contentToCopy);
	});
	closeButton.focus();
}

/**
 * The removeAlertBox function is used to revert the changes made by the showAlertBox function.
 * It sets the display property of the elements matching the contentToCopy selector to 'block', making them visible again.
 * It also removes the success message that was added by the showAlertBox function.
 *
 * @param {string} contentToCopy - A string representing the selector of the content that was copied. This is used to select the correct elements to make visible again.
 */
export function removeAlertBox(contentToRemove) {
	const contentToCopySelected = document.querySelectorAll(contentToRemove);
	for (const value of contentToCopySelected) {
		value.style.display = 'block';
	}
	const successMessage = document.querySelector('#eds-c-status-message--copy');
	if (successMessage) {
		successMessage.remove();
	}
}
